/**
 * utils/pboExtractor.js - Utilitaire générique extraction PBO
 * Traitement des noms PBO pour BaseROP et autres fichiers SOR
 */

import logger from "./logger.js";

/**
 * Types de PBO supportés
 */
const PBO_TYPES = {
  PBO_STANDARD: "PBO", // PBO-SRO-BPI-11549996-0041
  BE_STANDARD: "BE", // BE-SRO-BPI-11549996-SC-122256425-01
  UNKNOWN: "UNKNOWN", // Format non reconnu
};

/**
 * Patterns de reconnaissance PBO
 */
const PBO_PATTERNS = {
  PBO_STANDARD: /^PBO-SRO-BPI-\d+-\d{4}$/, // PBO-SRO-BPI-11549996-0041
  BE_STANDARD: /^BE-SRO-BPI-\d+-SC-\d+-\d{2}$/, // BE-SRO-BPI-11549996-SC-122256425-01
  PBO_FLEXIBLE: /^PBO-SRO-BPI-.*-(\d+)$/, // Plus flexible pour variations
  BE_FLEXIBLE: /^BE-SRO-BPI-.*$/, // Plus flexible pour BE
};

/**
 * Identifier le type de PBO
 * @param {string} pboValue - Valeur PBO brute
 * @returns {string} Type de PBO (PBO_TYPES)
 */
function _identifyPboType(pboValue) {
  if (!pboValue || typeof pboValue !== "string") {
    return PBO_TYPES.UNKNOWN;
  }

  const cleanValue = pboValue.trim().toUpperCase();

  // Test patterns stricts d'abord
  if (PBO_PATTERNS.PBO_STANDARD.test(cleanValue)) {
    return PBO_TYPES.PBO_STANDARD;
  }

  if (PBO_PATTERNS.BE_STANDARD.test(cleanValue)) {
    return PBO_TYPES.BE_STANDARD;
  }

  // Test patterns flexibles
  if (PBO_PATTERNS.PBO_FLEXIBLE.test(cleanValue)) {
    return PBO_TYPES.PBO_STANDARD;
  }

  if (PBO_PATTERNS.BE_FLEXIBLE.test(cleanValue)) {
    return PBO_TYPES.BE_STANDARD;
  }

  return PBO_TYPES.UNKNOWN;
}

/**
 * Extraire le numéro d'un PBO standard
 * @param {string} pboValue - Valeur PBO (PBO-SRO-BPI-11549996-0041)
 * @returns {number|null} Numéro extrait ou null si erreur
 */
function _extractPboNumber(pboValue) {
  if (!pboValue || typeof pboValue !== "string") {
    return null;
  }

  const cleanValue = pboValue.trim().toUpperCase();

  try {
    // Chercher le dernier segment après le dernier tiret
    const segments = cleanValue.split("-");
    if (segments.length < 2) {
      return null;
    }

    const lastSegment = segments[segments.length - 1];
    const number = parseInt(lastSegment, 10);

    // Vérifier que c'est un nombre valide
    if (isNaN(number) || number < 0) {
      return null;
    }

    return number;
  } catch (error) {
    logger.warn(
      {
        context: "pbo-number-extraction",
        error: error.message,
        pboValue,
        pboSegments: pboValue?.split?.("-") || [],
        attemptedOperation: "parseInt-last-segment",
      },
      `Erreur extraction numéro PBO: ${error.message}`
    );
    return null;
  }
}

/**
 * Normaliser un nom BE (format complet)
 * @param {string} beValue - Valeur BE brute
 * @returns {string|null} BE normalisé ou null si erreur
 */
function _normalizeBeName(beValue) {
  if (!beValue || typeof beValue !== "string") {
    return null;
  }

  try {
    // Nettoyer et mettre en majuscules
    const normalized = beValue.trim().toUpperCase();

    // Vérifier le format minimum
    if (!normalized.startsWith("BE-SRO-BPI-")) {
      return null;
    }

    return normalized;
  } catch (error) {
    logger.warn(
      {
        context: "be-name-normalization",
        error: error.message,
        beValue,
        startsWithBE: beValue?.trim?.()?.toUpperCase?.()?.startsWith?.("BE-SRO-BPI-") || false,
        attemptedOperation: "normalize-be-format",
      },
      `Erreur normalisation BE: ${error.message}`
    );
    return null;
  }
}

/**
 * Traiter une valeur PBO selon son type
 * @param {string} pboValue - Valeur PBO brute
 * @returns {Object} Résultat du traitement
 */
export function processPboValue(pboValue) {
  // if PBO value from excel is empty, or not in string format or equal to "0"
  if (!pboValue || typeof pboValue !== "string" || pboValue == "0") {
    return {
      isValid: false,
      type: PBO_TYPES.UNKNOWN,
      value: null,
      normalized: null,
      raw: pboValue,
      error: "Valeur PBO invalide ou vide",
    };
  }

  const cleanValue = pboValue.trim().toUpperCase();
  return {
    isValid: true,
    type: "dummy",
    value: cleanValue, // Numéro extrait (int)
    normalized: cleanValue,
    raw: cleanValue,
    error: null,
  };
}

/**
 * Valider un batch de valeurs PBO
 * @param {Array<string>} pboValues - Liste des valeurs PBO
 * @returns {Object} Statistiques de validation
 */
export function validatePboBatch(pboValues) {
  if (!Array.isArray(pboValues)) {
    return {
      total: 0,
      valid: 0,
      invalid: 0,
      byType: {},
      errors: ["Input n'est pas un tableau"],
    };
  }

  const results = pboValues.map(processPboValue);
  const stats = {
    total: results.length,
    valid: results.filter((r) => r.isValid).length,
    invalid: results.filter((r) => !r.isValid).length,
    byType: {},
    errors: [],
    samples: {
      valid: [],
      invalid: [],
    },
  };

  // Compter par type
  Object.values(PBO_TYPES).forEach((type) => {
    stats.byType[type] = results.filter((r) => r.type === type).length;
  });

  // Échantillons pour debug
  stats.samples.valid = results
    .filter((r) => r.isValid)
    .slice(0, 5)
    .map((r) => ({ raw: r.raw, type: r.type, value: r.value }));

  stats.samples.invalid = results
    .filter((r) => !r.isValid)
    .slice(0, 5)
    .map((r) => ({ raw: r.raw, error: r.error }));

  // Erreurs générales
  if (stats.invalid > stats.total * 0.5) {
    stats.errors.push(`Trop de PBO invalides: ${stats.invalid}/${stats.total}`);
  }

  return stats;
}
